"use client";

import { useEffect, useEffectEvent } from "react";

import { analyticsEvent } from "./functions";

/**
 * Attaches a delegated click listener to `target` that fires an
 * {@link analyticsEvent} for any clicked element carrying an `event-*` class.
 *
 * `target` is taken as a value (not a ref) so the listener re-attaches when the
 * element resolves — critical when the host resolves it asynchronously (e.g.
 * `document.getElementById` in an effect). Reading a ref once at mount would
 * miss a target populated by a later-running effect.
 */
export function useEventTracking(target: HTMLElement | null) {
  const handleEvent = useEffectEvent((event: Event) => {
    if (!(event.target instanceof Element)) return;

    const el = event.target.closest<HTMLElement>(
      "[class^='event-'], [class*=' event-']",
    );
    if (!el) return;

    const eventClass = Array.from(el.classList).find((c) =>
      c.startsWith("event-"),
    );
    if (!eventClass) return;

    const component = el
      .closest("[data-analytics-component]")
      ?.getAttribute("data-analytics-component");

    analyticsEvent({
      event: eventClass.replace(/^event-/, ""),
      ...(component && { component }),
    });
  });

  useEffect(() => {
    if (!target) return;

    target.addEventListener("click", handleEvent);
    return () => target.removeEventListener("click", handleEvent);
  }, [target]);
}
